import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Home, Building, UserCheck, DollarSign, Wrench, FileText, Star, CheckCircle, Shield, TrendingUp, Loader2 } from 'lucide-react';
import { useCMSContent } from '../hooks/useCMSContent';

export const HomePage = () => {
  const iconMap = {
    Home: Home,
    Building: Building,
    UserCheck: UserCheck,
    DollarSign: DollarSign,
    Wrench: Wrench,
    FileText: FileText
  };

  // Fetch CMS content
  const { data: companyInfo, loading: companyLoading } = useCMSContent('companyInfo');
  const { data: services, loading: servicesLoading } = useCMSContent('services');
  const { data: testimonials, loading: testimonialsLoading } = useCMSContent('testimonials');

  // Show loading state if critical content is loading
  if (companyLoading && !companyInfo) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Loader2 className="animate-spin" size={48} color="var(--brand-primary)" />
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: 'white' }} data-testid="home-page">
      {/* Hero Section - With Real Property Photo */}
      <section style={{
        position: 'relative',
        backgroundImage: 'url("https://customer-assets.emergentagent.com/job_3edf4311-b53d-4a6a-800b-8fbb025f103f/artifacts/tz0xdeny_LRGM%20Prop%206.jfif")',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        padding: '100px 0 80px',
        overflow: 'hidden'
      }}>
        {/* Dark Overlay for Text Readability */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, rgba(26, 58, 82, 0.88) 0%, rgba(37, 99, 235, 0.85) 100%)',
          zIndex: 0
        }} />

        <div className="container" style={{ position: 'relative', zIndex: 1, maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ maxWidth: '800px' }}>
            <h1 style={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 700,
              fontSize: 'clamp(2.5rem, 3rem + 0.5vw, 3.5rem)',
              lineHeight: 1.2,
              color: 'white',
              marginBottom: '24px'
            }} data-testid="hero-title">
              {companyInfo?.heroTitle || 'Professional Property Management Services'}
            </h1>
            <p style={{
              fontSize: '1.25rem',
              lineHeight: 1.7,
              color: 'rgba(255, 255, 255, 0.9)',
              marginBottom: '16px'
            }} data-testid="hero-subtitle">
              {companyInfo?.heroSubtitle || 'Protecting Your Investment, Maximizing Your Returns'}
            </p>
            <p style={{
              fontSize: '1.1rem',
              lineHeight: 1.7,
              color: 'rgba(255, 255, 255, 0.8)',
              marginBottom: '40px'
            }}>
              {companyInfo?.heroDescription || 'Comprehensive property management services for residential and commercial properties. We handle every aspect of your property so you don\'t have to.'}
            </p>
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              <Link to="/services" className="btn-primary" style={{ backgroundColor: 'white', color: 'var(--brand-primary)' }} data-testid="services-cta-btn">
                {companyInfo?.ctaButtonText1 || 'Our Services'}
                <ArrowRight size={20} style={{ marginLeft: '8px' }} />
              </Link>
              <Link to="/contact" className="btn-secondary" style={{ backgroundColor: 'transparent', borderColor: 'white', color: 'white', border: '2px solid white' }} data-testid="contact-cta-btn">
                {companyInfo?.ctaButtonText2 || 'Contact Us'}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section - No Blue Boxes */}
      <section className="section-spacing" style={{ backgroundColor: 'var(--bg-section-light)' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '50px', maxWidth: '800px', margin: '0 auto 50px' }}>
            <h2 className="heading-1" style={{ marginBottom: '16px' }}>
              Why Choose Laurel Run Management Group
            </h2>
            <p className="body-large">
              Professional property management backed by experience, integrity, and results
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '30px'
          }}>
            {[
              { icon: CheckCircle, title: 'Professional Expertise', desc: 'Decades of experience in residential and commercial property management' },
              { icon: Star, title: 'Proven Results', desc: 'Consistently high occupancy rates and satisfied property owners' },
              { icon: Shield, title: 'Asset Protection', desc: 'Comprehensive maintenance and tenant screening protects your investment' },
              { icon: TrendingUp, title: 'Maximum Returns', desc: 'Strategic pricing and cost management optimize your property income' }
            ].map((item, i) => (
              <div key={i} style={{
                backgroundColor: 'white',
                padding: '32px',
                borderRadius: '8px',
                border: '1px solid var(--border-light)',
                textAlign: 'left'
              }}>
                <item.icon size={40} color="var(--brand-primary)" strokeWidth={1.5} style={{ marginBottom: '20px' }} />
                <h3 style={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 600,
                  fontSize: '1.125rem',
                  color: 'var(--text-primary)',
                  marginBottom: '12px'
                }}>
                  {item.title}
                </h3>
                <p style={{
                  color: 'var(--text-secondary)',
                  fontSize: '0.9375rem',
                  lineHeight: 1.7
                }}>
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Property Showcase Section - Real Photos */}
      <section className="section-spacing">
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '50px' }}>
            <h2 className="heading-1" style={{ marginBottom: '16px' }}>
              Our Managed Properties
            </h2>
            <p className="body-large">
              Well-maintained residential communities throughout the region
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
            gap: '24px'
          }}>
            {[
              { img: 'https://customer-assets.emergentagent.com/job_3edf4311-b53d-4a6a-800b-8fbb025f103f/artifacts/l9bsgsrq_LRGM%20Prop%202.jpg', title: 'Multi-Family Residential' },
              { img: 'https://customer-assets.emergentagent.com/job_3edf4311-b53d-4a6a-800b-8fbb025f103f/artifacts/hcchv32x_LRGM%20Prop%203.jpg', title: 'Apartment Communities' },
              { img: 'https://customer-assets.emergentagent.com/job_3edf4311-b53d-4a6a-800b-8fbb025f103f/artifacts/okn41on4_LRGM%20Prop%204.webp', title: 'Residential Properties' }
            ].map((property, i) => (
              <div key={i} className="card-hover" style={{
                backgroundColor: 'white',
                borderRadius: '8px',
                overflow: 'hidden',
                border: '1px solid var(--border-light)'
              }}>
                <img 
                  src={property.img} 
                  alt={property.title}
                  style={{
                    width: '100%',
                    height: '280px',
                    objectFit: 'cover'
                  }}
                />
                <div style={{ padding: '24px' }}>
                  <h3 style={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 600,
                    fontSize: '1.125rem',
                    color: 'var(--text-primary)',
                    marginBottom: '8px'
                  }}>
                    {property.title}
                  </h3>
                  <p style={{
                    color: 'var(--text-secondary)',
                    fontSize: '0.9375rem'
                  }}>
                    Professional management and maintenance
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Overview - No Blue Boxes */}
      <section className="section-spacing" style={{ backgroundColor: 'var(--bg-section-light)' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '50px' }}>
            <h2 className="heading-1" style={{ marginBottom: '16px' }}>
              Our Property Management Services
            </h2>
            <p className="body-large">
              Comprehensive solutions for residential and commercial properties
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '24px'
          }} data-testid="services-grid">
            {(services || []).map((service) => {
              const Icon = iconMap[service.icon] || Home;
              return (
                <div
                  key={service.id || service.documentId}
                  className="card-hover"
                  style={{
                    backgroundColor: 'white',
                    padding: '32px',
                    borderRadius: '8px',
                    border: '1px solid var(--border-light)'
                  }}
                  data-testid={`service-card-${service.id || service.documentId}`}
                >
                  <Icon size={36} color="var(--brand-primary)" strokeWidth={1.5} style={{ marginBottom: '20px' }} />
                  <h3 style={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 600,
                    fontSize: '1.125rem',
                    color: 'var(--text-primary)',
                    marginBottom: '12px'
                  }}>
                    {service.title}
                  </h3>
                  <p style={{
                    color: 'var(--text-secondary)',
                    fontSize: '0.9375rem',
                    lineHeight: 1.7
                  }}>
                    {service.description}
                  </p>
                </div>
              );
            })}
          </div>

          <div style={{ textAlign: 'center', marginTop: '40px' }}>
            <Link to="/services" className="btn-primary">
              View All Services
              <ArrowRight size={20} style={{ marginLeft: '8px' }} />
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section-spacing">
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '50px' }}>
            <h2 className="heading-1" style={{ marginBottom: '16px' }}>
              What Property Owners Say
            </h2>
            <p className="body-large">
              Trusted by property owners throughout the region
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '24px'
          }} data-testid="testimonials-grid">
            {(testimonials || []).map((testimonial) => (
              <div
                key={testimonial.id || testimonial.documentId}
                style={{
                  backgroundColor: 'white',
                  padding: '32px',
                  borderRadius: '8px',
                  border: '1px solid var(--border-light)'
                }}
                data-testid={`testimonial-card-${testimonial.id || testimonial.documentId}`}
              >
                <div style={{ display: 'flex', gap: '4px', marginBottom: '16px' }}>
                  {[...Array(testimonial.rating || 5)].map((_, i) => (
                    <Star key={i} size={18} fill="var(--accent-orange)" color="var(--accent-orange)" />
                  ))}
                </div>
                <p style={{
                  color: 'var(--text-secondary)',
                  fontSize: '1rem',
                  lineHeight: 1.7,
                  marginBottom: '20px'
                }}>
                  "{testimonial.text}"
                </p>
                <div>
                  <p style={{
                    color: 'var(--text-primary)',
                    fontWeight: 600,
                    fontSize: '0.9375rem',
                    marginBottom: '4px'
                  }}>
                    {testimonial.name}
                  </p>
                  <p style={{
                    color: 'var(--text-muted)',
                    fontSize: '0.875rem'
                  }}>
                    {testimonial.role}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section style={{
        background: 'linear-gradient(135deg, #1a3a52 0%, #2563eb 100%)',
        padding: '80px 0',
        textAlign: 'center'
      }} data-testid="cta-section">
        <div className="container">
          <h2 style={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 700,
            fontSize: 'clamp(2rem, 2.5rem + 0.5vw, 2.75rem)',
            color: 'white',
            marginBottom: '16px'
          }}>
            Ready to Get Started?
          </h2>
          <p style={{
            fontSize: '1.125rem',
            color: 'rgba(255, 255, 255, 0.9)',
            marginBottom: '32px',
            maxWidth: '700px',
            margin: '0 auto 32px'
          }}>
            Contact us today to learn how we can help protect and maximize your property investment
          </p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/contact" className="btn-primary" style={{ backgroundColor: 'white', color: 'var(--brand-primary)' }} data-testid="cta-contact-btn">
              Contact Us Today
              <ArrowRight size={20} style={{ marginLeft: '8px' }} />
            </Link>
            <a href={`mailto:${companyInfo?.email || 'Office@lrmgmt.net'}`} className="btn-secondary" style={{ backgroundColor: 'transparent', borderColor: 'white', color: 'white', border: '2px solid white' }} data-testid="cta-email-btn">
              Email Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};
